import React, { useCallback, useEffect, useState } from 'react';

import * as modeTypeStorage from '../lib/storage/modeType';

type Mode = modeTypeStorage.mode;

type ModeContextProps = {
  // Mode defines the user's selected mode
  mode: Mode | null;
  // setMode sets a new mode in context and stores it in local storage
  setMode: (mode: Mode) => void;
  // removeMode removes the mode from context and local storage
  removeMode: () => void;
};

const ModeContext = React.createContext<ModeContextProps | null>(null);

/**
 * ModeContextProvider provides the mode context to its children
 */
export function ModeContextProvider({ children }: React.PropsWithChildren<{}>) {
  const [mode, setModeState] = useState(modeTypeStorage.get());

  // Persist the mode to local storage when the mode changes. Remove the mode
  // if it is null.
  useEffect(() => {
    if (mode) {
      modeTypeStorage.set(mode);
    } else {
      modeTypeStorage.remove();
    }
  }, [mode]);

  const removeMode = useCallback(() => {
    setModeState(null);
  }, []);

  const setMode = useCallback((m: Mode) => {
    setModeState(m);
  }, []);

  const ctx = {
    mode,
    setMode,
    removeMode,
  };

  return <ModeContext.Provider value={ctx}>{children}</ModeContext.Provider>;
}

export default ModeContext;
